import {useState} from 'react';
import DocumentTypeSelect from "./DocumentTypeSelect";
import CountrySelect from "./CountrySelect";
import DocumentCameraPrompt from "./DocumentCameraPrompt";
import {IdFrontGizmo} from "../../gizmos/IdFrontGizmo";
import {IdBackGizmo} from "../../gizmos/IdBackGizmo";
import {PassportGizmo} from "../../gizmos/PassportGizmo";
import {trackEvent} from "../../../utils/analytics";

function DocumentUpload({inputChange, field, verification}) {
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedDocumentType, setSelectedDocumentType] = useState(null);
    const [currentSideIndex, setCurrentSideIndex] = useState(0);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [fileInputs, setFileInputs] = useState([]);

    const {galleryRestriction, configuredDocumentTypes} = verification?.settings.reduce(
        (acc, setting) => {
            switch (setting.fieldName) {
                case "GALLERY_RESTRICTION":
                    acc.galleryRestriction = setting;
                    break;
                case "DOCUMENT_TYPES":
                    acc.configuredDocumentTypes = JSON.parse(setting.value);
                    break;
                default:
                    break;
            }
            return acc;
        },
        {
            galleryRestriction: null,
            configuredDocumentTypes: null,
        }
    );

    const handleCountrySelect = (country) => {
        const countryJson = JSON.parse(country);
        trackEvent("document_country_selected", {
            flowId: verification?.flowId,
            verificationId: verification?.verificationId,
            country: countryJson?.code,
        });
        setSelectedCountry(countryJson);
        setSelectedDocumentType(null);
        setCurrentStepIndex(0);
        setCurrentSideIndex(0);
        setFileInputs([]);
    };

    const handleDocumentTypeSelect = (documentType) => {
        const docTypeJson = JSON.parse(documentType)
        trackEvent("document_type_selected", {
            flowId: verification?.flowId,
            verificationId: verification?.verificationId,
            documentType: docTypeJson?.name,
        });
        setSelectedDocumentType(docTypeJson);
        setCurrentSideIndex(0);
    };

    const handleInputChange = (inputData) => {
        const updatedFileInputs = [...fileInputs, inputData];
        setFileInputs(updatedFileInputs);

        // Move to the next side if there are more sides to capture
        if (currentSideIndex < selectedDocumentType.sides.length - 1) {
            setCurrentSideIndex(currentSideIndex + 1);
        } else {
            if (currentStepIndex < configuredDocumentTypes.length - 1) {
                setCurrentStepIndex(currentStepIndex + 1);
                setSelectedDocumentType(null)
            }else{
                // All sides have been captured
                inputChange(updatedFileInputs);
            }
        }
    };

    const goBackFromUpload = () => {
        setSelectedDocumentType(null)
        setCurrentSideIndex(0);
        setCurrentStepIndex(0);
    }

    const goBackFromDocumentSelect = () => {
        setSelectedDocumentType(null)
        setSelectedCountry(null)
        setCurrentSideIndex(0);
        setCurrentStepIndex(0);
    }

    const renderCurrentDocumentPrompt = () => {
        if (selectedDocumentType && selectedDocumentType.sides && verification) {
            const side = selectedDocumentType.sides[currentSideIndex];
            const isPassport = selectedDocumentType.name === "PASSPORT";
            const illustration = isPassport
                ? <PassportGizmo/>
                : side === "FRONT"
                    ? <IdFrontGizmo height={200} width={200}/>
                    : <IdBackGizmo height={200} width={200}/>
            return (
                <DocumentCameraPrompt
                    goback={goBackFromUpload}
                    key={side}
                    country={selectedCountry}
                    restrictGallery={galleryRestriction}
                    type={selectedDocumentType.name}
                    title={`${selectedDocumentType.label}`}
                    inputChange={handleInputChange}
                    fieldName={field}
                    illustration={illustration}
                    side={side}
                    selectedDocument={selectedDocumentType.name}
                    verification={verification}
                    process={"DOCUMENT_VERIFICATION"}
                />
            );
        }
        return null;
    };


    return (
        <div className="h-full w-full flex flex-col">
            {!selectedCountry && (
                <CountrySelect onSelect={handleCountrySelect}/>
            )}
            {selectedCountry && !selectedDocumentType && (
                <DocumentTypeSelect
                stepNumber={configuredDocumentTypes[currentStepIndex].step}
                totalSteps={configuredDocumentTypes.length}
                country={selectedCountry}
                goback={goBackFromDocumentSelect}
                onSelect={handleDocumentTypeSelect}
            />
            )}
            {selectedDocumentType && renderCurrentDocumentPrompt()}
        </div>
    );
}

export default DocumentUpload;
